import React, { ReactElement } from 'react';
import { Modal } from '@odin-labs/components';
import { EnterPinStep, ConfirmPinStep } from './components';

export type SetPinDrawerProps = {
  isOpen: boolean;
  closeDrawer: () => void;
  hasExistingPin: boolean;
  isLocked?: boolean;
  onPinSet: () => void;
};

type Step = 'enter' | 'confirm';

export function SetPinDrawer(props: SetPinDrawerProps): ReactElement {
  const {
    isOpen,
    closeDrawer,
    hasExistingPin,
    isLocked = false,
    onPinSet,
  } = props;

  const [step, setStep] = React.useState<Step>('enter');
  const [capturedPin, setCapturedPin] = React.useState('');

  // Shared by `afterLeave` (drawer fully closed) and `onBack` (user clicks
  // the back arrow on step 2). Both want the same end state: empty
  // captured PIN, step machine back at 'enter'. The ConfirmPinStep's own
  // `confirmInput` state clears automatically on unmount.
  const returnToEnter = React.useCallback((): void => {
    setStep('enter');
    setCapturedPin('');
  }, []);

  const onPinCaptured = React.useCallback((pin: string): void => {
    setCapturedPin(pin);
    setStep('confirm');
  }, []);

  const enterTitle = ((): string => {
    if (isLocked) return 'Unlock device PIN';
    if (hasExistingPin) return 'Change device PIN';
    return 'Set device PIN';
  })();

  return (
    <Modal
      open={isOpen}
      setOpen={closeDrawer}
      afterLeave={returnToEnter}
      variant="drawer"
      size="xs"
      title={step === 'enter' ? enterTitle : 'Confirm PIN'}
      actionsPanel={null}
    >
      {step === 'enter' ? (
        <EnterPinStep
          closeDrawer={closeDrawer}
          isLocked={isLocked}
          onPinCaptured={onPinCaptured}
        />
      ) : (
        <ConfirmPinStep
          closeDrawer={closeDrawer}
          hasExistingPin={hasExistingPin}
          capturedPin={capturedPin}
          onBack={returnToEnter}
          onPinSet={onPinSet}
        />
      )}
    </Modal>
  );
}
